/**
 * Web Media Engine
 * 
 * Implementation of IMediaEngine that wraps Vidstack player for web/browser playback.
 * Used as fallback on web platform and can be used alongside native engine on mobile.
 */

import type { MediaPlayerInstance } from "@vidstack/react";
import type {
	IMediaEngine,
	MediaSource,
	MediaMetadata,
	MediaEngineConfig,
	MediaEngineCallbacks,
	PlaybackState,
	PlaybackPosition,
} from "./types";

export class WebMediaEngine implements IMediaEngine {
	private player: MediaPlayerInstance | null = null;
	private callbacks: MediaEngineCallbacks = {};
	private readonly _config: MediaEngineConfig;
	private state: PlaybackState = "idle";
	private _currentSource: MediaSource | null = null;
	private _metadata: MediaMetadata | null = null;
	private timeUpdateInterval: number | null = null;
	private mediaSessionCleanup: (() => void) | null = null;

	public readonly isNative = false;

	public constructor(config: MediaEngineConfig) {
		this._config = config;
	}

	public get currentSource(): MediaSource | null {
		return this._currentSource;
	}

	/**
	 * Attach a Vidstack player instance to this engine.
	 * Must be called after the MediaPlayer component mounts.
	 */
	public attachPlayer(player: MediaPlayerInstance): void {
		this.player = player;
		this.setupPlayerListeners();
		// If already in playing state (from handlePlay before attach), start polling now
		if (this.state === "playing") {
			this.startTimeUpdatePolling();
		}
		console.log("[WebMediaEngine] Player attached");
	}

	/**
	 * Detach the current player instance
	 */
	public detachPlayer(): void {
		this.stopTimeUpdatePolling();
		this.cleanupMediaSession();
		this.player = null;
		this.state = "idle";
		console.log("[WebMediaEngine] Player detached");
	}

	private setupPlayerListeners(): void {
		if (!this.player) return;

		// Note: In the refactored components, we'll wire these up via props
		// This is a helper for when you need programmatic control
	}

	private setState(newState: PlaybackState): void {
		if (this.state !== newState) {
			this.state = newState;
			this.callbacks.onStateChange?.(newState);
		}
	}

	public async load(source: MediaSource, metadata?: MediaMetadata): Promise<void> {
		this._currentSource = source;
		this._metadata = metadata || null;
		this.setState("loading");
		this.callbacks.onLoadStart?.();

		// Setup Web MediaSession for lock screen controls
		if (metadata) {
			this.setupMediaSession(metadata);
		}

		// Note: Actual loading is handled by Vidstack via React props
		// This method prepares the engine state
		console.log("[WebMediaEngine] Source prepared for loading:", source.src);
		
		// Satisfy interface - actual loading is handled by React component
		await Promise.resolve();
	}

	public async play(): Promise<void> {
		if (!this.player) {
			console.warn("[WebMediaEngine] No player attached");
			return;
		}

		try {
			await this.player.play();
			this.setState("playing");
			this.updateMediaSessionState("playing");
			this.startTimeUpdatePolling();
		} catch (error) {
			console.error("[WebMediaEngine] Play failed:", error);
			this.setState("error");
			this.callbacks.onError?.(error instanceof Error ? error : new Error(String(error)));
		}
	}

	public async pause(): Promise<void> {
		if (!this.player) return;

		try {
			await this.player.pause();
			this.setState("paused");
			this.updateMediaSessionState("paused");
		} catch (error) {
			console.error("[WebMediaEngine] Pause failed:", error);
		}
	}

	public async seek(time: number): Promise<void> {
		if (!this.player) return;

		const duration = this.player.duration || 0;
		const clampedTime = Math.max(0, Math.min(duration, time));
		this.player.currentTime = clampedTime;
		
		// Update position state for media session
		this.updateMediaSessionPosition();
		
		// Satisfy interface requirement
		await Promise.resolve();
	}

	public async stop(): Promise<void> {
		if (!this.player) return;

		await this.pause();
		await this.seek(0);
		this.stopTimeUpdatePolling();
		this.setState("idle");
		this.updateMediaSessionState("none");
	}

	public getPosition(): PlaybackPosition {
		if (!this.player) {
			return { currentTime: 0, duration: 0, playbackRate: 1 };
		}

		// Vidstack player properties are getters that can throw if internal media element isn't ready
		try {
			const position = {
				currentTime: this.player.currentTime || 0,
				duration: this.player.duration || 0,
				playbackRate: this.player.playbackRate || 1,
			};
			return position;
		} catch {
			// Player exists but internal state is invalid - return defaults
			return { currentTime: 0, duration: 0, playbackRate: 1 };
		}
	}

	public getState(): PlaybackState {
		return this.state;
	}

	public setVolume(volume: number): void {
		if (this.player) {
			this.player.volume = Math.max(0, Math.min(1, volume));
		}
	}

	public setPlaybackRate(rate: number): void {
		if (this.player) {
			this.player.playbackRate = rate;
		}
	}

	public updateMetadata(metadata: MediaMetadata): void {
		this._metadata = metadata;
		this.setupMediaSession(metadata);
	}

	public setCallbacks(callbacks: MediaEngineCallbacks): void {
		this.callbacks = callbacks;
	}

	public destroy(): void {
		this.stopTimeUpdatePolling();
		this.cleanupMediaSession();
		this.detachPlayer();
		this._currentSource = null;
		this._metadata = null;
		console.log("[WebMediaEngine] Destroyed");
	}

	// ============================================
	// Event handlers (called from React component)
	// ============================================

	/**
	 * Call this from the component's onCanPlay handler
	 */
	public handleCanPlay(): void {
		this.setState("ready");
		this.callbacks.onCanPlay?.();
		
		if (this.player) {
			this.callbacks.onReady?.(this.player.duration || 0);
		}
	}

	/**
	 * Call this from the component's onPlay handler
	 */
	public handlePlay(): void {
		this.setState("playing");
		this.updateMediaSessionState("playing");
		// Only start polling if player is attached
		if (this.player) {
			this.startTimeUpdatePolling();
		}
	}

	/**
	 * Call this from the component's onPause handler
	 */
	public handlePause(): void {
		this.setState("paused");
		this.updateMediaSessionState("paused");
	}

	/**
	 * Call this from the component's onEnded handler
	 */
	public handleEnded(): void {
		this.setState("ended");
		this.stopTimeUpdatePolling();
		this.updateMediaSessionState("none");
		this.callbacks.onEnded?.();
	}

	/**
	 * Call this from the component's onError handler
	 */
	public handleError(error: unknown): void {
		this.setState("error");
		this.callbacks.onError?.(error instanceof Error ? error : new Error(String(error)));
	}

	/**
	 * Call this from the component's onTimeUpdate handler
	 */
	public handleTimeUpdate(): void {
		const position = this.getPosition();
		this.callbacks.onTimeUpdate?.(position);
	}

	// ============================================
	// Web MediaSession API (for lock screen controls)
	// ============================================

	private setupMediaSession(metadata: MediaMetadata): void {
		if (!("mediaSession" in navigator)) {
			console.warn("[WebMediaEngine] MediaSession API not available");
			return;
		}

		try {
			navigator.mediaSession.metadata = new MediaMetadata({
				title: metadata.title,
				artist: metadata.artist || "Akademia LTCA",
				album: metadata.album || "Kursy LTCA",
				artwork: metadata.artwork || [
					{ src: "/assets/logo-only.png", sizes: "512x512", type: "image/png" },
				],
			});

			// Setup action handlers
			const handlers: Array<[MediaSessionAction, () => void]> = [
				["play", (): void => void this.play()],
				["pause", (): void => void this.pause()],
				["seekbackward", (): void => {
					const pos = this.getPosition();
					void this.seek(pos.currentTime - 10);
				}],
				["seekforward", (): void => {
					const pos = this.getPosition();
					void this.seek(pos.currentTime + 10);
				}],
				["seekto", (): void => {
					// Note: seekto details come from the event, but we handle it via direct seek calls
				}],
			];

			for (const [action, handler] of handlers) {
				try {
					navigator.mediaSession.setActionHandler(action, handler);
				} catch {
					console.warn(`[WebMediaEngine] Action ${action} not supported`);
				}
			}

			// Store cleanup function
			this.mediaSessionCleanup = (): void => {
				for (const [action] of handlers) {
					try {
						navigator.mediaSession.setActionHandler(action, null);
					} catch {
						// Ignore cleanup errors
					}
				}
				if (navigator.mediaSession) {
					navigator.mediaSession.metadata = null;
					navigator.mediaSession.playbackState = "none";
				}
			};

			console.log("[WebMediaEngine] MediaSession configured");
		} catch (error) {
			console.warn("[WebMediaEngine] Failed to setup MediaSession:", error);
		}
	}

	private updateMediaSessionState(state: "playing" | "paused" | "none"): void {
		if ("mediaSession" in navigator) {
			try {
				navigator.mediaSession.playbackState = state;
				if (state === "playing" || state === "paused") {
					this.updateMediaSessionPosition();
				}
			} catch (error) {
				console.warn("[WebMediaEngine] Failed to update MediaSession state:", error);
			}
		}
	}

	private updateMediaSessionPosition(): void {
		if (!("mediaSession" in navigator) || !this.player) return;

		try {
			const position = this.getPosition();
			if (position.duration > 0) {
				navigator.mediaSession.setPositionState?.({
					duration: position.duration,
					position: position.currentTime,
					playbackRate: position.playbackRate,
				});
			}
		} catch (error) {
			console.warn("[WebMediaEngine] Failed to update position state:", error);
		}
	}

	private cleanupMediaSession(): void {
		if (this.mediaSessionCleanup) {
			this.mediaSessionCleanup();
			this.mediaSessionCleanup = null;
		}
	}

	// ============================================
	// Time Update Polling
	// ============================================

	private startTimeUpdatePolling(): void {
		// Don't start polling if no player is attached
		if (!this.player) {
			return;
		}
		
		this.stopTimeUpdatePolling();
		
		// Poll every 250ms for smooth UI updates
		this.timeUpdateInterval = window.setInterval(() => {
			// Additional safety check - stop polling if player was detached
			if (!this.player) {
				this.stopTimeUpdatePolling();
				return;
			}
			if (this.state === "playing") {
				this.handleTimeUpdate();
			}
		}, 250);
	}

	private stopTimeUpdatePolling(): void {
		if (this.timeUpdateInterval !== null) {
			clearInterval(this.timeUpdateInterval);
			this.timeUpdateInterval = null;
		}
	}
}

/**
 * Create a WebMediaEngine instance
 */
export function createWebMediaEngine(config: MediaEngineConfig): WebMediaEngine {
	return new WebMediaEngine(config);
}
